// Only canonical editorial links between the two publisher brands participate.
// Query/fragment links, modifiers, new tabs, downloads and product routes keep
// their native navigation. The DOM href remains clean (including copy-link).
const EDITORIAL_PATH_PATTERN = /^\/(?:[a-z0-9]+(?:-[a-z0-9]+)*\/){0,7}[a-z0-9]+(?:-[a-z0-9]+)+\/?$/;
const BRANDS = { 'narcity.com': 'narcity', 'mtlblog.com': 'mtlblog' };
const BRIDGE_INTENT_TIMEOUT_MS = 2000;

function buildBridgeUrl({ issuer, pageHost, destination, route }) {
  try {
    const auth = new URL(issuer);
    const match = auth.hostname.match(/^auth(?:-dev|-sandbox)?\.(narcity\.com|mtlblog\.com)$/);
    if (!match || auth.protocol !== 'https:' || auth.port || auth.username || auth.password) return null;
    const sourceSite = match[1];
    if (![`www.${sourceSite}`, `linkinbio.${sourceSite}`].includes(pageHost)) return null;
    const targetSite = sourceSite === 'narcity.com' ? 'mtlblog.com' : 'narcity.com';
    const url = new URL(destination);
    if (url.origin !== `https://www.${targetSite}` || url.username || url.password
      || url.search || url.hash || url.pathname.length > 1024 || !EDITORIAL_PATH_PATTERN.test(url.pathname)
      || ['/__milla/', '/api/', '/oauth/', '/callback', '/.well-known/'].some((prefix) => url.pathname.startsWith(prefix))) return null;
    const returnToken = btoa(url.pathname).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    const start = new URL(route, auth.origin);
    start.searchParams.set('to', BRANDS[targetSite]);
    start.searchParams.set('rt', 'editorial');
    start.searchParams.set('r', returnToken);
    return start.toString();
  } catch (_) {
    return null;
  }
}

// Treat the response as untrusted routing input: same environment, opposite
// auth host, exact consume route, exact path token, one ephemeral ticket only.
function validatedConsumeUrl(value, bridge) {
  try {
    const start = new URL(bridge);
    const expectedHost = start.hostname.replace(/\.(narcity|mtlblog)\.com$/, `.${start.searchParams.get('to')}.com`);
    const url = new URL(value);
    if (url.origin !== `https://${expectedHost}` || url.username || url.password || url.hash
      || url.pathname !== '/__milla/bridge/consume'
      || [...url.searchParams.keys()].sort().join(',') !== 'bt,r'
      || !/^[A-Za-z0-9_-]{43}$/.test(url.searchParams.get('bt') || '')
      || url.searchParams.get('r') !== start.searchParams.get('r')) return null;
    return url.toString();
  } catch (_) {
    return null;
  }
}

function installCrossBrandBridge({ getState, route, isOptedOut }) {
  if (typeof window === 'undefined' || typeof document === 'undefined' || !document.addEventListener) return () => {};
  const ownerWindow = window;
  const ownerDocument = document;
  let pending = null;
  let disposed = false;
  const eligible = (state) => state.flags.bootstrap && state.flags.crossBrandBridge
    && state.status.identityState === 'DURABLE' && !isOptedOut();
  const cancel = () => {
    if (!pending) return;
    clearTimeout(pending.timer);
    pending.controller.abort();
    pending = null;
  };
  const onClick = async (event) => {
    if (!event.isTrusted || event.defaultPrevented || event.button !== 0
      || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = event.target?.closest?.('a[href]');
    if (pending && anchor?.href === pending.destination) {
      event.preventDefault(); // Coalesce double clicks; never issue twice.
      return;
    }
    cancel(); // Another choice must not be hijacked by a late response.
    const state = getState();
    if (disposed || !eligible(state) || ownerDocument.prerendering || ownerDocument.visibilityState === 'hidden'
      || ownerWindow.top !== ownerWindow.self
      || ownerWindow.navigator?.userActivation?.isActive === false
      || typeof ownerWindow.fetch !== 'function' || !ownerWindow.AbortController || !ownerDocument.body) return;
    if (!anchor || anchor.hasAttribute('download') || anchor.hasAttribute('ping')
      || anchor.getAttribute('data-milla-bridge') === 'off'
      || (anchor.target && anchor.target.toLowerCase() !== '_self')) return;
    const bridge = buildBridgeUrl({ issuer: state.issuer, pageHost: ownerWindow.location.hostname, destination: anchor.href, route });
    if (!bridge) return;
    event.preventDefault();
    const start = new URL(bridge);
    const operation = { destination: anchor.href, issuer: state.issuer, page: ownerWindow.location.href, controller: new ownerWindow.AbortController() };
    pending = operation;
    try {
      const timeout = new Promise((resolve) => {
        operation.timer = setTimeout(() => { operation.controller.abort(); resolve(null); }, BRIDGE_INTENT_TIMEOUT_MS);
      });
      // Nothing is prefetched. The HttpOnly source cookie authenticates this
      // click-only JSON request; Safari need not supply Sec-Fetch-User.
      const request = ownerWindow.fetch(start.origin + start.pathname, {
        method: 'POST', mode: 'cors', credentials: 'include', cache: 'no-store', redirect: 'error', referrerPolicy: 'no-referrer',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schemaVersion: 1, ...Object.fromEntries(start.searchParams) }),
        signal: operation.controller.signal,
      }).then((response) => response.ok ? response.json() : null).catch(() => null);
      const result = await Promise.race([request, timeout]);
      if (pending !== operation || disposed || ownerWindow.location.href !== operation.page) return;
      const current = getState();
      const consume = current.issuer === operation.issuer && eligible(current) && result?.schemaVersion === 1
        ? validatedConsumeUrl(result.navigateTo, bridge) : null;
      // Fetch response headers cannot set the publisher document's referrer
      // policy. Apply it to this navigation explicitly; do not mutate hrefs
      // on the real editorial links or store tickets in browser storage.
      const navigation = ownerDocument.createElement('a');
      navigation.href = consume || operation.destination;
      navigation.referrerPolicy = 'no-referrer';
      navigation.rel = 'noreferrer';
      navigation.target = '_self';
      navigation.hidden = true;
      ownerDocument.body.appendChild(navigation);
      navigation.click();
      navigation.remove();
    } catch (_) {
      if (pending === operation && !disposed && ownerWindow.location.href === operation.page) ownerWindow.location.assign(operation.destination);
    } finally {
      clearTimeout(operation.timer);
      if (pending === operation) pending = null;
    }
  };
  ownerDocument.addEventListener('click', onClick);
  for (const name of ['pagehide', 'popstate', 'hashchange']) ownerWindow.addEventListener?.(name, cancel);
  return () => {
    disposed = true;
    cancel();
    ownerDocument.removeEventListener?.('click', onClick);
    for (const name of ['pagehide', 'popstate', 'hashchange']) ownerWindow.removeEventListener?.(name, cancel);
  };
}

module.exports = { BRIDGE_INTENT_TIMEOUT_MS, EDITORIAL_PATH_PATTERN, buildBridgeUrl, validatedConsumeUrl, installCrossBrandBridge };
